import { FC } from 'react';
import {
	useSupergridMutations, getLevelDepth, getLevelByDepth,
} from '@aleph/supergrid';
import {
	generateMutationHash,
	SPOTIFY_DEFAULT_CREATE_VALUES,
	SpotifyCreateLevels,
	SpotifySuperGridMutationsConfig,
} from 'wb-spotify/app/routes/superGrid/mutator/utils';
import { startCase } from 'lodash';
import {
	Button, ButtonAppearance,
	ButtonColor,
	ButtonSize,
	PlusIcon, Tooltip,
} from '@aleph/shared-components';
import { SPOTIFY_SUPERGRID_CONFIG } from 'wb-spotify/app/routes/superGrid/config';
import classNames from 'classnames';
import { ICellRendererParams } from 'ag-grid-community';
import { SpotifyLevel } from 'wb-spotify/app/routes/superGrid/utils';

interface IAddCellRendererParams extends ICellRendererParams {
	parentData: Record<string, any>
}

const SpotifyVerticalAddCellRenderer: FC<IAddCellRendererParams> = (params) => {
	const { addMutation, mutations } = useSupergridMutations<SpotifySuperGridMutationsConfig>();

	const onAddEntity = () => {
		let currLevelDepth = getLevelDepth(SPOTIFY_SUPERGRID_CONFIG, params.data?.level);
		let creatingLevel = getLevelByDepth(SPOTIFY_SUPERGRID_CONFIG, currLevelDepth);
		let currParent = params.parentData;

		while (creatingLevel !== SpotifyLevel.BREAKDOWN) {
			const newHash = generateMutationHash();

			const newMutation = {
				...(SPOTIFY_DEFAULT_CREATE_VALUES[creatingLevel]?.(currParent) ?? {}),
				name:       `New ${startCase(creatingLevel)} ${(mutations?.[creatingLevel as SpotifyLevel]?.length ?? 0) + 1}`,
				currency:   currParent?.currency,
				parent:     currParent,
				parentHash: currParent?.hash,
				parentId:   currParent?.id,
				hash:       newHash,
			};

			addMutation(creatingLevel as unknown as SpotifyCreateLevels, newMutation);

			currParent = newMutation;
			creatingLevel = getLevelByDepth(SPOTIFY_SUPERGRID_CONFIG, ++currLevelDepth);
		}
	};

	switch (params.data?.level) {
		case SpotifyLevel.CAMPAIGN:
		case SpotifyLevel.GROUP:
			if (params.rowIndex > 0 || (params.node.rowPinned)) {
				return null;
			}

			break;
		case SpotifyLevel.AD:
			if (params.node.rowPinned !== 'top' || params.data?.fieldName === 'Group') {
				return null;
			}

			break;
	}

	return (
		<div className={classNames(
			'flex justify-center h-full',
			params.data?.level !== SpotifyLevel.AD ? 'items-center' : 'items-start pt-[12px]',
		)}
		>
			<Tooltip
				content={<span>Add new in {startCase(params?.parentData?.name)}</span>}
				placement="top"
				offset={10}
			>
				<Button
					onClick={onAddEntity}
					leftNode={<PlusIcon/>}
					appearance={ButtonAppearance.GHOST}
					size={ButtonSize.SMALL}
					color={ButtonColor.GRAY}
				/>
			</Tooltip>
			<div />
		</div>
	);
};

export default SpotifyVerticalAddCellRenderer;
